import { Autocomplete, Box, Paper, TextField, Typography } from "@mui/material"
import { StyledButton } from "components/Button/StyledButton"
import { ReportNodeImageBox } from "components/ReportNodeImage/ReportNodeImageBox"
import { ReportNodeTableBox } from "components/ReportNodeTable/ReportNodeTableBox"
import { ReportNodeTextBox } from "components/ReportNodeText/ReportNodeTextBox"
import { Criteria } from "graphql/types"
import { useState } from "react"
import { isNodeImage, isNodeTable, isNodeText } from "store/typeGuards"
import { v4 } from "uuid"
import { CriteriaEditDialog } from "./CriteriaEditDialog"

type CriteriaListProps = {
  criteriaList: Criteria[]
  onSaveCriteria: (criteria: Criteria) => void
  onDeleteCriteria: (index: number) => void
  operatingModes: string[]
}

export const CriteriaList = (props: CriteriaListProps) => {
  const { criteriaList, onSaveCriteria, onDeleteCriteria, operatingModes } = props

  const [editingCriteria, setEditingCriteria] = useState<Criteria | null>(null)

  const handleAddCriteria = () => {
    const newCriteria: Criteria = {
      id: v4(),
      name: "",
      description: [],
      operatingModes: [],
    }
    setEditingCriteria(newCriteria)
  }

  const handleEditCriteria = (criteria: Criteria) => {
    setEditingCriteria(criteria)
  }

  const handleSaveCriteria = (criteria: Criteria) => {
    onSaveCriteria(criteria)
    setEditingCriteria(null)
  }

  const handleCloseCriteriaDialog = () => {
    setEditingCriteria(null)
  }

  const handleOperatingModesChange = (criteria: Criteria, selectedModes: string[]) => {
    const updatedCriteria = {
      ...criteria,
      operatingModes: selectedModes,
    }
    onSaveCriteria(updatedCriteria)
  }

  return (
    <Paper sx={{ mt: 2, p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">Kriterienliste</Typography>
        <StyledButton
          buttonType="add"
          customText="Kriterium hinzufügen"
          onClick={handleAddCriteria}
        />
      </Box>
      {criteriaList.length > 0 ? (
        <Box>
          {criteriaList.map((criteria, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",

                mb: 2,
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {criteria.name || "Unbenanntes Kriterium"}
                </Typography>
                {operatingModes.length > 0 && (
                  <Autocomplete
                    multiple
                    size="small"
                    options={operatingModes}
                    value={criteria.operatingModes || []}
                    onChange={(_, value) => handleOperatingModesChange(criteria, value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Operating Modes auswählen..."
                        fullWidth
                      />
                    )}
                    sx={{ mb: 2 }}
                  />
                )}
                {criteria.description.length > 0 && (
                  <Box>
                    {criteria.description.map((item) => (
                      <Box
                        key={item.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        {isNodeText(item) && <ReportNodeTextBox data={item} />}
                        {isNodeTable(item) && <ReportNodeTableBox data={item} />}
                        {isNodeImage(item) && <ReportNodeImageBox data={item} />}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <StyledButton
                  buttonType="edit"
                  size="small"
                  iconOnly={true}
                  onClick={() => handleEditCriteria(criteria)}
                />
                <StyledButton
                  buttonType="delete"
                  size="small"
                  iconOnly={true}
                  onClick={() => onDeleteCriteria(index)}
                />
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Keine Kriterien hinzugefügt. Verwenden Sie den Button oben, um Kriterien hinzuzufügen.
        </Typography>
      )}

      <CriteriaEditDialog
        open={!!editingCriteria}
        onClose={handleCloseCriteriaDialog}
        criteria={editingCriteria}
        onSave={handleSaveCriteria}
      />
    </Paper>
  )
}
